import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Phone,
    Mail,
    MapPin,
    Hammer,
    Wrench,
    Droplets,
    Plug,
    SquareDashed,
    PaintRoller,
    DoorOpen,
    Frame,
    LayoutGrid,
    Home as HomeIcon,
    Star,
    Clock,
    ShieldCheck,
    ArrowUpRight,
    Check,
    Facebook,
    Wind,
    Sun,
    Thermometer,
    Bath,
    Tv,
    Flame,
    Leaf,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { SITE, SERVICES, SERVICE_CAPABILITIES, TESTIMONIALS } from "@/lib/site";

// Precomputed once at module load — avoids re-filtering on every render.
const BIG_SERVICES = SERVICES.filter((s) => s.big);
import { Header, AnnouncementBar, Footer } from "@/components/Layout";
import { Link } from "react-router-dom";
import { useSeo, breadcrumbSchema } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
    Hammer, Wrench, Droplets, Plug, SquareDashed, PaintRoller,
    DoorOpen, Frame, LayoutGrid, Home: HomeIcon, HomeIcon,
};

/* ---------- Hero ---------- */
const Hero = () => (
    <section id="top" className="relative bg-[#F6F5F2] border-b border-[#E2DFD8] overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 pt-12 pb-16 md:pt-20 md:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 rise">
                <div className="inline-flex items-center gap-2 mb-6 border border-[#111111] px-3 py-1.5" data-testid="hero-badge">
                    <span className="w-1.5 h-1.5 bg-[#E11D2A]" />
                    <span className="font-['Manrope'] text-xs font-bold tracking-[0.2em] uppercase text-[#111111]">Serving Southern Oregon since day one</span>
                </div>
                <div className="inline-flex items-center gap-2 mb-6 ml-2 bg-[#FFD400] border border-[#111111] px-3 py-1.5" data-testid="hero-ccb-badge">
                    <span className="font-['Manrope'] text-xs font-black tracking-[0.18em] uppercase text-[#111111]">Licensed General Contractor · CCB #232477</span>
                </div>
                <h1 className="font-['Anton'] font-normal tracking-tight leading-[0.95] text-[#111812] text-6xl sm:text-7xl lg:text-8xl uppercase">
                    Medford&apos;s most
                    <br />
                    reliable{" "}
                    <span className="relative inline-block">
                        <span className="relative z-10">handyman.</span>
                        <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#E11D2A]/35 -z-0" />
                    </span>
                </h1>
                <p className="mt-7 font-['Manrope'] text-lg md:text-xl leading-relaxed text-[#5F5F5F] max-w-2xl">
                    Plumbing, electrical, drywall, paint, doors, windows, flooring, roof
                    repairs, and full manufactured-home setup &amp; repair. One call,
                    done right — the first time.
                </p>
                <div className="mt-9 flex flex-col sm:flex-row gap-4">
                    <a href={SITE.phoneHref} data-testid="hero-call-cta">
                        <Button className="rounded-none h-14 px-7 bg-[#E11D2A] hover:bg-[#A14227] text-white font-bold tracking-wide text-base font-['Manrope'] shadow-block-sm">
                            <Phone className="w-5 h-5 mr-3" strokeWidth={2.5} />
                            CALL {SITE.phoneDisplay}
                        </Button>
                    </a>
                    <a href="#contact" data-testid="hero-quote-cta">
                        <Button variant="outline" className="rounded-none h-14 px-7 border-2 border-[#111111] bg-transparent text-[#111111] hover:bg-[#111111] hover:text-[#F6F5F2] font-bold tracking-wide text-base font-['Manrope']">
                            REQUEST A FREE QUOTE
                            <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                        </Button>
                    </a>
                </div>
                <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
                    {[
                        { i: ShieldCheck, t: "CCB #232477 · Bonded · Insured" },
                        { i: Clock, t: "On-time, every time" },
                        { i: Star, t: "5-star local reputation" },
                    ].map(({ i: I, t }) => (
                        <div key={t} className="flex items-center gap-2">
                            <I className="w-4 h-4 text-[#E11D2A]" strokeWidth={2.5} />
                            <span className="font-['Manrope'] text-sm font-semibold text-[#111812]">{t}</span>                        </div>
                    ))}
                </div>
            </div>

            <div className="lg:col-span-5 relative">
                <div className="relative aspect-[4/5] w-full overflow-hidden border border-[#111111] shadow-block">
                    <img src="https://customer-assets.emergentagent.com/job_service-connect-473/artifacts/pv1avps0_169283925_4300748906601942_2889365670962161350_n.jpg" alt="5 Star Construction Services GMC truck and enclosed trailer at a finished manufactured home in Southern Oregon" className="absolute inset-0 w-full h-full object-cover object-center" />
                    <div className="absolute inset-x-0 bottom-0 bg-[#111111] text-[#F6F5F2] p-5">
                        <div className="font-['Manrope'] text-xs uppercase tracking-[0.22em] text-[#E11D2A] font-bold">Local · Independent</div>
                        <div className="font-display text-2xl font-extrabold mt-1">Built for Southern Oregon homes.</div>
                    </div>
                </div>
                <div className="absolute -top-4 -left-4 hidden md:block bg-[#E11D2A] text-white p-4 border border-[#111111] shadow-block-sm">
                    <div className="font-display text-3xl font-black leading-none">11+</div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-bold mt-1">Service Specialties</div>
                </div>
            </div>
        </div>

        {/* Marquee */}
        <div className="border-y-2 border-[#111111] bg-[#111111] text-[#F6F5F2] overflow-hidden">
            <div className="flex animate-marquee whitespace-nowrap py-4">
                {["a", "b"].map((k) => (
                    <div key={`marquee-loop-${k}`} className="flex items-center gap-12 pr-12">
                        {["Plumbing", "Electrical", "Drywall", "Paint", "Doors", "Windows", "Flooring", "Roof Repairs", "Manufactured Homes", "Leak Repairs"].map((w) => (
                            <span key={`${w}-${k}`} className="flex items-center gap-12">
                                <span className="font-display font-extrabold text-2xl tracking-tight">{w}</span>
                                <span className="text-[#E11D2A] font-display font-black">★</span>
                            </span>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    </section>
);

/* ---------- Services ---------- */
const ServiceBanner = ({ s }) => {
    const Icon = ICONS[s.icon] || Hammer;
    return (
        <article
            className="relative bg-white border border-[#E2DFD8] hover:border-[#111111] hover:shadow-block-sm hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all p-6 md:p-10 grid md:grid-cols-12 gap-6 md:gap-10 items-start"
            data-testid={`service-banner-${s.key}`}
        >
            <div className="md:col-span-4 lg:col-span-3 flex md:block items-center gap-4">
                <div className="w-14 h-14 md:w-20 md:h-20 bg-[#E11D2A] text-white border border-[#111111] flex items-center justify-center shrink-0">
                    <Icon className="w-7 h-7 md:w-10 md:h-10" strokeWidth={2.25} />
                </div>
                <h3 className="md:mt-6 font-display font-extrabold tracking-tight text-[#111812] text-2xl md:text-4xl lg:text-5xl leading-[0.95]">
                    {s.title}
                </h3>
            </div>
            <div className="md:col-span-8 lg:col-span-9">
                <p className="font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                    {s.blurb}
                </p>
                {s.bullets && (
                    <ul className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2 pt-6 border-t border-[#E2DFD8]">
                        {s.bullets.map((b) => (
                            <li key={b} className="flex items-start gap-2 text-sm md:text-base font-semibold text-[#111812]">
                                <Check className="w-4 h-4 text-[#E11D2A] mt-1 shrink-0" strokeWidth={3} />
                                <span>{b}</span>
                            </li>
                        ))}
                    </ul>
                )}
                {s.key === "full" && (
                    <a
                        href="#full-capability-list"
                        className="inline-flex items-center gap-2 mt-6 font-['Manrope'] text-sm font-bold tracking-wide uppercase text-[#E11D2A] hover:text-[#B0121E] transition-colors border-b-2 border-[#E11D2A] pb-1"
                        data-testid="see-full-list-link"
                    >
                        + See the full capability list below
                        <ArrowUpRight className="w-4 h-4 rotate-90" strokeWidth={2.5} />
                    </a>
                )}
            </div>
        </article>
    );
};

/* ---------- Featured Specialty Services (Decks + Fans + Solartubes) ---------- */
const SUMMER_PROMO = [
    {
        key: "decks",
        label: "Decks & Repairs",
        img: "/images/decks/backyard-deck.jpg",
        href: "/decks",
    },
    {
        key: "fans",
        label: "Whole House Fans",
        img: "/images/fans/whole-house-fan-motion.jpg",
        href: "/fans",
    },
    {
        key: "solartubes",
        label: "Solartube Skylights",
        img: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b4/Exemple_d%27installation_d%27un_conduit_de_lumi%C3%A8re_naturelle_dans_une_cuisine_-_SOLATUBE.jpg/1280px-Exemple_d%27installation_d%27un_conduit_de_lumi%C3%A8re_naturelle_dans_une_cuisine_-_SOLATUBE.jpg",
        href: "/solar-tubes",
    },
];

const SPECIALTY_TILES = [
    {
        key: "roof-demossing",
        title: "Roof De-Mossing",
        blurb: "Same-day roof moss removal — hand-scrape, soft-wash, moss-killer treatment. Most competitive prices in OR + WA.",
        icon: Leaf,
        img: "/images/roof/moss-shingles.jpg",
        href: "/roof-demossing",
    },
    {
        key: "bathroom-remodel",
        title: "Bathroom Remodels",
        blurb: "Southern Oregon's premier bathroom remodel company. Cultured marble, fiberglass, and full transformations.",
        icon: Bath,
        img: "/images/bathroom/hero.jpg",
        href: "/bathroom-remodel",
    },
    {
        key: "media-walls",
        title: "Media Walls",
        blurb: "Custom wood, shiplap, or drywall media walls with built-in electric fireplaces and mounted TVs.",
        icon: Tv,
        img: "/images/media-walls/hero.jpg",
        href: "/media-walls",
    },
    {
        key: "decks",
        title: "Decks & Repairs",
        blurb: "New decks, additions, full structural repairs, board replacements, railings & stairs. Built strong, built right, built to last.",
        icon: Hammer,
        img: "/images/decks/finished-deck.jpg",
        href: "/decks",
    },
    {
        key: "whole-house-fans",
        title: "Whole House Fans",
        blurb: "Cool the whole house in 15 minutes for pennies on the dollar. 50–90% less than running A/C.",
        icon: Wind,
        img: "/images/fans/whole-house-fan-installed.jpg",
        href: "/fans",
    },
    {
        key: "bath-fans",
        title: "Bathroom Fans",
        blurb: "Quiet, code-compliant retrofits. Fan-only, light + fan, or light + heat + fan combos.",
        icon: Droplets,
        img: "/images/fans/bathroom-ceiling-vent.jpg",
        href: "/fans",
    },
    {
        key: "solartubes",
        title: "Solartube Skylights",
        blurb: "Pipe real daylight into any windowless room. Sun tunnels / tubular skylights, installed in a day.",
        icon: Sun,
        img: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b4/Exemple_d%27installation_d%27un_conduit_de_lumi%C3%A8re_naturelle_dans_une_cuisine_-_SOLATUBE.jpg/1280px-Exemple_d%27installation_d%27un_conduit_de_lumi%C3%A8re_naturelle_dans_une_cuisine_-_SOLATUBE.jpg",
        href: "/solar-tubes",
    },
];

const FeaturedSpecialties = () => (
    <div className="mt-16 md:mt-20">
        {/* NEW: Roof De-Mossing banner — top position for max visibility */}
        <Link
            to="/roof-demossing"
            data-testid="home-roof-banner"
            className="group relative block mb-6 overflow-hidden border-2 border-[#111111] shadow-block hover:shadow-block-lg transition-all"
        >
            <div className="grid lg:grid-cols-12 min-h-[260px] lg:min-h-[300px]">
                {/* Text side */}
                <div className="lg:col-span-7 relative bg-[#111111] text-[#F6F5F2] p-7 md:p-10 lg:p-12 flex flex-col justify-center overflow-hidden">
                    <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #FFD400 1px, transparent 0)", backgroundSize: "14px 14px" }} />
                    <div className="relative">
                        <div className="inline-flex items-center gap-2 mb-4 bg-[#E11D2A] text-white px-3 py-1.5">
                            <Leaf className="w-4 h-4" strokeWidth={2.5} />
                            <span className="font-['Manrope'] text-xs font-black tracking-[0.22em] uppercase">New service · OR + WA</span>
                        </div>
                        <h3 className="font-['Anton'] tracking-tight leading-[0.92] text-3xl sm:text-4xl md:text-5xl lg:text-6xl uppercase">
                            Roof caked in moss?
                            <br />
                            <span className="text-[#FFD400]">Same-day fix.</span>
                        </h3>
                        <p className="mt-4 font-['Manrope'] text-base md:text-lg text-[#F6F5F2]/85 max-w-2xl leading-relaxed">
                            <strong className="text-[#F6F5F2]">Most competitive prices</strong> in the PNW · <strong className="text-[#F6F5F2]">same-day transformations</strong> · hand-scrape + soft-wash + moss-killer treatment. Serving Medford, Southern Oregon, Portland, Vancouver, Battle Ground &amp; surrounding areas.
                        </p>
                        <div className="mt-6 inline-flex items-center gap-3 font-['Manrope'] text-sm md:text-base font-black tracking-wide uppercase text-[#FFD400] group-hover:text-white transition-colors">
                            <span className="border-b-2 border-[#FFD400] group-hover:border-white pb-0.5">See roof de-mossing service</span>
                            <ArrowUpRight className="w-5 h-5" strokeWidth={2.75} />
                        </div>
                    </div>
                </div>
                {/* Image side — static before/after composite */}
                <div className="lg:col-span-5 relative aspect-[16/10] lg:aspect-auto bg-[#111111] overflow-hidden">
                    <div className="absolute inset-0 grid grid-cols-2" data-testid="home-roof-before-after">
                        <div className="relative overflow-hidden">
                            <img
                                src="/images/roof/gen-before.jpg"
                                alt="Moss-covered asphalt shingle roof — before 5 Star Construction moss removal"
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute top-3 left-3 bg-[#E11D2A] text-white px-2.5 py-1 font-['Manrope'] text-[10px] font-black tracking-[0.22em] uppercase">Before</div>
                        </div>
                        <div className="relative overflow-hidden border-l-2 border-[#111111]">
                            <img
                                src="/images/roof/gen-after.jpg"
                                alt="Clean, moss-free residential roof — after 5 Star Construction moss removal"
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute top-3 right-3 bg-[#FFD400] text-[#111111] px-2.5 py-1 font-['Manrope'] text-[10px] font-black tracking-[0.22em] uppercase">After</div>
                        </div>
                    </div>
                    <div className="absolute top-4 right-4 bg-[#FFD400] text-[#111111] px-3 py-1.5 border-2 border-[#111111] z-10 hidden sm:block">
                        <div className="font-['Manrope'] text-[10px] uppercase tracking-[0.22em] font-black leading-none">Same day</div>
                        <div className="font-display text-sm font-extrabold mt-1 leading-none">Free quotes</div>
                    </div>
                </div>
            </div>
        </Link>

        {/* Summer-ready banner — 3 service images combined */}
        <div className="relative overflow-hidden border-2 border-[#111111] shadow-block bg-[#FFD400]" data-testid="home-summer-banner">
            <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #111111 1px, transparent 0)", backgroundSize: "14px 14px" }} />
            <div className="relative grid lg:grid-cols-12 gap-0">
                {/* Text side */}
                <div className="lg:col-span-5 p-7 md:p-10 lg:p-12 flex flex-col justify-center">
                    <div className="inline-flex items-center gap-2 mb-4 self-start bg-[#111111] text-[#FFD400] px-3 py-1.5">
                        <Thermometer className="w-4 h-4" strokeWidth={2.5} />
                        <span className="font-['Manrope'] text-xs font-black tracking-[0.22em] uppercase">Summer-ready upgrades</span>
                    </div>
                    <h3 className="font-['Anton'] tracking-tight leading-[0.92] text-[#111111] text-4xl sm:text-5xl lg:text-6xl uppercase">
                        Get ready
                        <br />
                        for <span className="bg-[#E11D2A] text-[#F6F5F2] px-3 py-0.5">summer.</span>
                    </h3>
                    <p className="mt-5 font-['Manrope'] text-base md:text-lg text-[#111111]/85 leading-relaxed">
                        Three quick upgrades that change how you live in your house all summer long — a <strong>new deck</strong> to use the backyard, a <strong>whole house fan</strong> to cool it in 15 minutes, and a <strong>solartube</strong> to drown the dark rooms in sunlight.
                    </p>
                    <a href="#contact" data-testid="home-summer-cta" className="mt-7 inline-flex items-center gap-3 self-start font-['Manrope'] text-sm md:text-base font-black tracking-wide uppercase text-[#111111] hover:text-[#E11D2A] transition-colors group">
                        <span className="border-b-2 border-[#111111] group-hover:border-[#E11D2A] pb-0.5">Get a free quote</span>
                        <ArrowUpRight className="w-5 h-5" strokeWidth={2.75} />
                    </a>
                </div>
                {/* 3-up image grid side */}
                <div className="lg:col-span-7 grid grid-cols-3 gap-0 border-t-2 lg:border-t-0 lg:border-l-2 border-[#111111]">
                    {SUMMER_PROMO.map(({ key, label, img, href }, idx) => (
                        <Link
                            key={key}
                            to={href}
                            data-testid={`home-summer-tile-${key}`}
                            className={`group relative aspect-[3/4] sm:aspect-[4/5] lg:aspect-auto lg:min-h-[420px] overflow-hidden bg-[#111111] ${idx > 0 ? "border-l-2 border-[#111111]" : ""}`}
                        >
                            <img
                                src={img}
                                alt={label}
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/85 via-[#111111]/15 to-transparent pointer-events-none" />
                            <div className="absolute inset-x-3 bottom-3 sm:inset-x-4 sm:bottom-4 text-[#F6F5F2]">
                                <div className="font-['Anton'] uppercase tracking-tight leading-[0.95] text-xl sm:text-2xl lg:text-3xl">
                                    {label}
                                </div>
                                <div className="mt-2 inline-flex items-center gap-1.5 font-['Manrope'] text-[10px] sm:text-xs font-black tracking-[0.2em] uppercase text-[#FFD400] group-hover:text-white transition-colors">
                                    Learn more
                                    <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.75} />
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </div>

        {/* Premier bathroom remodel banner */}
        <Link
            to="/bathroom-remodel"
            data-testid="home-bathroom-banner"
            className="group relative block mt-6 overflow-hidden border-2 border-[#111111] shadow-block hover:shadow-block-lg transition-all"
        >
            <div className="grid lg:grid-cols-12 min-h-[240px] lg:min-h-[280px]">
                {/* Image side */}
                <div className="lg:col-span-5 relative aspect-[16/10] lg:aspect-auto bg-[#111111] overflow-hidden">
                    <img
                        src="/images/bathroom/hero.jpg"
                        alt="Modern bathroom remodel with glass shower, stone walls, and sleek white vanity — Southern Oregon"
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                </div>
                {/* Text side */}
                <div className="lg:col-span-7 relative bg-[#111111] text-[#F6F5F2] p-7 md:p-10 lg:p-12 flex flex-col justify-center overflow-hidden">
                    <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #FFD400 1px, transparent 0)", backgroundSize: "14px 14px" }} />
                    <div className="relative">
                        <div className="inline-flex items-center gap-2 mb-4 bg-[#FFD400] text-[#111111] px-3 py-1.5">
                            <Bath className="w-4 h-4" strokeWidth={2.5} />
                            <span className="font-['Manrope'] text-xs font-black tracking-[0.22em] uppercase">Southern Oregon&apos;s Premier</span>
                        </div>
                        <h3 className="font-['Anton'] tracking-tight leading-[0.92] text-3xl sm:text-4xl md:text-5xl lg:text-6xl uppercase">
                            Bathroom Remodel
                            <br />
                            <span className="text-[#FFD400]">Company.</span>
                        </h3>
                        <p className="mt-4 font-['Manrope'] text-base md:text-lg text-[#F6F5F2]/80 max-w-2xl leading-relaxed">
                            Cultured marble showers, fiberglass surrounds, and full bathroom remodels — the Rogue Valley&apos;s trusted contractor since 25 years ago.
                        </p>
                        <div className="mt-6 inline-flex items-center gap-3 font-['Manrope'] text-sm md:text-base font-black tracking-wide uppercase text-[#FFD400] group-hover:text-white transition-colors">
                            <span className="border-b-2 border-[#FFD400] group-hover:border-white pb-0.5">See our bathroom remodels</span>
                            <ArrowUpRight className="w-5 h-5" strokeWidth={2.75} />
                        </div>
                    </div>
                </div>
            </div>
        </Link>

        {/* 6 Specialty service tiles */}
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SPECIALTY_TILES.map(({ key, title, blurb, icon: I, img, href }) => (
                <Link
                    key={key}
                    to={href}
                    data-testid={`home-specialty-${key}`}
                    className="group relative bg-white border border-[#E2DFD8] hover:border-[#111111] hover:shadow-block-sm hover:-translate-y-0.5 transition-all flex flex-col overflow-hidden"
                >
                    <div className="aspect-[4/3] overflow-hidden border-b border-[#E2DFD8] group-hover:border-[#111111] bg-[#111111] relative">
                        <img
                            src={img}
                            alt={title}
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3 w-10 h-10 bg-[#FFD400] border border-[#111111] flex items-center justify-center">
                            <I className="w-5 h-5 text-[#111111]" strokeWidth={2.5} />
                        </div>
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                        <h4 className="font-display font-extrabold tracking-tight text-[#111812] text-xl md:text-2xl">
                            {title}
                        </h4>
                        <p className="mt-2 font-['Manrope'] text-sm text-[#5F5F5F] leading-relaxed flex-1">{blurb}</p>
                        <div className="mt-5 inline-flex items-center gap-2 font-['Manrope'] text-xs font-black tracking-[0.18em] uppercase text-[#E11D2A] group-hover:text-[#111111] transition-colors">
                            Learn more
                            <ArrowUpRight className="w-4 h-4" strokeWidth={2.75} />
                        </div>
                    </div>
                </Link>
            ))}
        </div>
    </div>
);

const Services = () => (
    <section id="services" className="bg-[#F6F5F2] border-b border-[#E2DFD8]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-32">
            <div className="grid lg:grid-cols-12 gap-8 mb-14">
                <div className="lg:col-span-5">
                    <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">What we do</div>
                    <h2 className="mt-4 font-display font-extrabold tracking-tight text-[#111812] text-4xl sm:text-5xl">
                        One crew. The whole punch list.
                    </h2>
                </div>
                <div className="lg:col-span-7 lg:pt-3">
                    <p className="font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                        We handle the small fixes most contractors won&apos;t bother with — and the bigger jobs you need a real pro for. From a leaking trap under the sink to releveling a manufactured home, we show up on time and finish the job.
                    </p>
                </div>
            </div>

            <div className="space-y-5">
                {BIG_SERVICES.map((s) => (
                    <ServiceBanner key={s.key} s={s} />
                ))}
            </div>

            <FeaturedSpecialties />

            {/* Full Capability List */}
            <div id="full-capability-list" className="mt-24 md:mt-32 scroll-mt-24">
                <div className="grid lg:grid-cols-12 gap-8 mb-12">
                    <div className="lg:col-span-5">
                        <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Full capability list</div>
                        <h3 className="mt-4 font-display font-extrabold tracking-tight text-[#111812] text-3xl sm:text-4xl">
                            And everything else on the punch list.
                        </h3>
                    </div>
                    <div className="lg:col-span-7 lg:pt-2">
                        <p className="font-['Manrope'] text-base text-[#5F5F5F] leading-relaxed">
                            Drywall, paint, plumbing, doors, floors, fences, fixtures, appliances — if it&apos;s in or around the house, we probably handle it. Don&apos;t see your project below? Call us anyway. The list keeps growing.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-[#E2DFD8] border border-[#E2DFD8]">
                    {SERVICE_CAPABILITIES.map((cat) => (
                        <div key={cat.group} className="bg-white p-6 md:p-7" data-testid={`capability-${cat.group.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
                            <div className="flex items-center gap-2 pb-4 mb-4 border-b border-[#E2DFD8]">
                                <span className="w-2 h-2 bg-[#E11D2A]" />
                                <h4 className="font-['Manrope'] text-xs font-black tracking-[0.18em] uppercase text-[#111812]">{cat.group}</h4>
                            </div>
                            <ul className="space-y-2.5">
                                {cat.items.map((it) => (
                                    <li key={it} className="flex items-start gap-2 text-sm text-[#111812] leading-snug">
                                        <Check className="w-4 h-4 text-[#E11D2A] mt-0.5 shrink-0" strokeWidth={3} />
                                        <span>{it}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-[#111111] text-[#F6F5F2] p-6 md:p-7 border border-[#111111]">
                    <div className="flex-1">
                        <div className="font-display text-xl md:text-2xl font-extrabold">Don&apos;t see it on the list?</div>
                        <div className="font-['Manrope'] text-sm md:text-base text-[#F6F5F2]/75 mt-1">If it&apos;s broken, leaking, hanging crooked, or just driving you nuts — give us a call. Odds are we fix it.</div>
                    </div>
                    <a href={SITE.phoneHref} data-testid="capabilities-call-cta">
                        <Button className="rounded-none h-12 px-6 bg-[#E11D2A] hover:bg-[#B0121E] text-white font-bold tracking-wide font-['Manrope']">
                            <Phone className="w-4 h-4 mr-2" strokeWidth={2.5} />
                            CALL {SITE.phoneDisplay}
                        </Button>
                    </a>
                </div>
            </div>
        </div>
    </section>
);

/* ---------- About ---------- */
const About = () => (
    <section id="about" className="bg-white border-b border-[#E2DFD8]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 grid grid-cols-2 gap-4">
                <div className="aspect-[3/4] overflow-hidden border border-[#111111] shadow-block">
                    <img src="https://customer-assets.emergentagent.com/job_service-connect-473/artifacts/mdpijdm2_175320090_4337683859575113_970944133164417820_n.jpg" alt="Finished manufactured home setup in the Southern Oregon pines by 5 Star Construction" className="w-full h-full object-cover object-center" />
                </div>
                <div className="aspect-[3/4] overflow-hidden border border-[#111111] mt-10">
                    <img src="https://customer-assets.emergentagent.com/job_service-connect-473/artifacts/ssqtsfkk_5efc99a8-2a8a-4bac-aaf5-a3bd2d840198.jpg" alt="5 Star Construction Services work van on a Southern Oregon job site with framing lumber and manufactured home" className="w-full h-full object-cover object-[center_60%]" />
                </div>
            </div>
            <div className="lg:col-span-6">
                <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Why choose us</div>
                <h2 className="mt-4 font-display font-extrabold tracking-tight text-[#111812] text-4xl sm:text-5xl">
                    A local crew that actually answers the phone.
                </h2>
                <p className="mt-6 font-['Manrope'] text-base md:text-lg text-[#5F5F5F] leading-relaxed">
                    5 Star Construction Services LLC is family-run out of Medford, Oregon. We&apos;re hands-on craftsmen — not a call center handing your job off to whoever&apos;s cheapest. We quote what it&apos;ll cost, we show up when we said, and we leave the place cleaner than we found it.
                </p>
                <div className="mt-10 grid sm:grid-cols-2 gap-5">
                    {[
                        { t: "Licensed, Bonded & Insured", d: "Full coverage on every job, every site." },
                        { t: "Honest, Up-Front Pricing", d: "Real numbers — no surprise add-ons." },
                        { t: "Manufactured Home Specialists", d: "Setup, leveling, skirting, full repairs." },
                        { t: "Local to Southern Oregon", d: "Medford, Ashland, Grants Pass and beyond." },
                    ].map((it) => (
                        <div key={it.t} className="border-l-2 border-[#E11D2A] pl-4 py-1">
                            <div className="font-display text-lg font-bold text-[#111812]">{it.t}</div>
                            <div className="font-['Manrope'] text-sm text-[#5F5F5F] mt-1">{it.d}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </section>
);

/* ---------- Testimonials ---------- */
const Testimonials = () => (
    <section id="reviews" className="relative bg-[#111111] text-[#F6F5F2] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
            <img src="https://images.pexels.com/photos/12932247/pexels-photo-12932247.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#111111]/95 to-[#000000]/95" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-32">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
                <div>
                    <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Customer reviews</div>
                    <h2 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl max-w-2xl">
                        The kind of work neighbors actually talk about.
                    </h2>
                </div>
                <div className="flex items-center gap-1">
                    {[0, 1, 2, 3, 4].map((i) => (
                        <Star key={i} className="w-7 h-7 fill-[#E11D2A] text-[#E11D2A]" />
                    ))}
                    <span className="ml-3 font-display text-2xl font-extrabold">5.0</span>
                </div>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
                {TESTIMONIALS.map((t, idx) => (
                    <article key={t.name} className="bg-[#F6F5F2] text-[#111812] p-7 border border-[#000000]" data-testid={`testimonial-${idx}`}>
                        <div className="flex gap-0.5 mb-4">
                            {[0, 1, 2, 3, 4].map((i) => (
                                <Star key={i} className="w-4 h-4 fill-[#E11D2A] text-[#E11D2A]" />
                            ))}
                        </div>
                        <p className="font-['Manrope'] text-base leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                        <div className="mt-6 pt-5 border-t border-[#E2DFD8]">
                            <div className="font-display text-base font-bold">{t.name}</div>
                            <div className="font-['Manrope'] text-xs uppercase tracking-[0.18em] text-[#5F5F5F] mt-1">{t.location}</div>
                        </div>
                    </article>
                ))}
            </div>
        </div>
    </section>
);

/* ---------- Contact ---------- */
const Contact = () => {
    const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
    const [submitting, setSubmitting] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.phone || !form.service || !form.message) {
            toast.error("Please fill in name, phone, service, and message.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = { ...form };
            if (!payload.email) delete payload.email;
            await axios.post(`${API}/contact`, payload);
            toast.success("Thanks! We'll be in touch within one business day.");
            setForm({ name: "", phone: "", email: "", service: "", message: "" });
        } catch (err) {
            toast.error("Couldn't send your request. Please call us directly.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="contact" className="bg-[#F6F5F2] border-b border-[#E2DFD8]">
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-32 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <div className="font-['Manrope'] text-sm font-bold tracking-[0.22em] uppercase text-[#E11D2A]">Get in touch</div>
                    <h2 className="mt-4 font-display font-extrabold tracking-tight text-[#111812] text-4xl sm:text-5xl">
                        Have a project? Pick up the phone.
                    </h2>
                    <a href={SITE.phoneHref} className="block mt-10 group" data-testid="contact-phone-block">
                        <div className="font-['Manrope'] text-xs uppercase tracking-[0.22em] font-bold text-[#5F5F5F]">Call or text</div>
                        <div className="font-display font-black text-[#111111] text-5xl sm:text-6xl tracking-tighter mt-2 group-hover:text-[#E11D2A] transition-colors">
                            {SITE.phoneDisplay}
                        </div>
                    </a>
                    <div className="mt-8 space-y-4">
                        <div className="flex items-start gap-3">
                            <Mail className="w-5 h-5 text-[#E11D2A] mt-1" strokeWidth={2.25} />
                            <div>
                                <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#5F5F5F]">Email</div>
                                <div className="font-display text-lg font-bold text-[#111812]">{SITE.emailDisplay}</div>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <Facebook className="w-5 h-5 text-[#E11D2A] mt-1" strokeWidth={2.25} />
                            <div>
                                <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#5F5F5F]">Facebook</div>
                                <a href={SITE.facebookUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-facebook" className="font-display text-lg font-bold text-[#111812] hover:text-[#E11D2A] transition-colors">@5starservicellc</a>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <MapPin className="w-5 h-5 text-[#E11D2A] mt-1" strokeWidth={2.25} />
                            <div>
                                <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#5F5F5F]">Service Area</div>
                                <div className="font-display text-lg font-bold text-[#111812]">Medford &amp; Southern Oregon</div>
                                <div className="font-['Manrope'] text-sm text-[#5F5F5F] mt-1">{SITE.serviceArea.join(" · ")}</div>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <Clock className="w-5 h-5 text-[#E11D2A] mt-1" strokeWidth={2.25} />
                            <div>
                                <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#5F5F5F]">Hours</div>
                                {SITE.hours.map((h) => (
                                    <div key={h.day} className="font-['Manrope'] text-sm text-[#111812]">
                                        <span className="font-bold">{h.day}</span> · {h.time}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <form onSubmit={submit} className="lg:col-span-7 bg-white border border-[#111111] shadow-block p-7 md:p-10" data-testid="contact-form">
                    <div className="font-display text-2xl font-extrabold text-[#111812]">Request a free quote</div>
                    <p className="font-['Manrope'] text-sm text-[#5F5F5F] mt-2">We&apos;ll reach out within one business day.</p>
                    <div className="mt-8 grid sm:grid-cols-2 gap-5">
                        <div>
                            <Label className="font-['Manrope'] text-xs uppercase tracking-[0.18em] font-bold text-[#111111]">Name *</Label>
                            <Input data-testid="contact-input-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Jane Doe" className="rounded-none mt-2 h-12 border-[#111111] focus:ring-0 focus:border-[#E11D2A]" />
                        </div>
                        <div>
                            <Label className="font-['Manrope'] text-xs uppercase tracking-[0.18em] font-bold text-[#111111]">Phone *</Label>
                            <Input data-testid="contact-input-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(541) 555-0100" className="rounded-none mt-2 h-12 border-[#111111] focus:ring-0 focus:border-[#E11D2A]" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="font-['Manrope'] text-xs uppercase tracking-[0.18em] font-bold text-[#111111]">Email (optional)</Label>
                            <Input data-testid="contact-input-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" className="rounded-none mt-2 h-12 border-[#111111] focus:ring-0 focus:border-[#E11D2A]" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="font-['Manrope'] text-xs uppercase tracking-[0.18em] font-bold text-[#111111]">Service needed *</Label>
                            <Select value={form.service} onValueChange={(v) => update("service", v)}>
                                <SelectTrigger data-testid="contact-select-service" className="rounded-none mt-2 h-12 border-[#111111] focus:ring-0">
                                    <SelectValue placeholder="Select a service" />
                                </SelectTrigger>
                                <SelectContent className="rounded-none">
                                    {SERVICES.map((s) => (
                                        <SelectItem key={s.key} value={s.title} className="rounded-none">{s.title}</SelectItem>
                                    ))}
                                    <SelectItem value="Other / Not sure" className="rounded-none">Other / Not sure</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="font-['Manrope'] text-xs uppercase tracking-[0.18em] font-bold text-[#111111]">Tell us about the job *</Label>
                            <Textarea data-testid="contact-input-message" rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="What needs fixing? Any timing details?" className="rounded-none mt-2 border-[#111111] focus:ring-0 focus:border-[#E11D2A]" />
                        </div>
                    </div>
                    <Button data-testid="contact-submit" type="submit" disabled={submitting} className="rounded-none h-14 px-8 mt-8 bg-[#111111] hover:bg-[#000000] text-white font-bold tracking-wide text-base font-['Manrope']">
                        {submitting ? "Sending..." : "Send my request"}
                        <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                    </Button>
                    <p className="font-['Manrope'] text-xs text-[#5F5F5F] mt-4">
                        Or just call us directly at <a href={SITE.phoneHref} className="font-bold text-[#E11D2A]" data-testid="contact-form-phone-link">{SITE.phoneDisplay}</a>.
                    </p>
                </form>
            </div>
        </section>
    );
};

/* ---------- For Pros Callout (medium strip) ---------- */
const ForProsCallout = () => (
    <section id="for-pros" className="bg-[#111111] text-[#F6F5F2] border-y-4 border-[#E11D2A]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-12 md:py-14 grid lg:grid-cols-12 gap-6 lg:gap-10 items-center">
            <div className="lg:col-span-8">
                <div className="inline-flex items-center gap-2 mb-4 border border-[#E11D2A] px-3 py-1.5">
                    <span className="text-[#FFD400] font-display font-black">★</span>
                    <span className="font-['Manrope'] text-xs font-bold tracking-[0.22em] uppercase text-[#E11D2A]">For property pros</span>
                </div>
                <h2 className="font-['Anton'] uppercase tracking-tight leading-[0.95] text-3xl sm:text-4xl md:text-5xl">
                    Property manager, HOA, or realtor?
                    <br className="hidden sm:block" />
                    <span className="text-[#FFD400]">We&apos;ve got a dedicated page.</span>
                </h2>
                <p className="mt-4 font-['Manrope'] text-base md:text-lg text-[#F6F5F2]/75 max-w-2xl">
                    Turnover maintenance · tenant work orders · inspection repairs · curb-appeal upgrades.
                </p>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-stretch">
                <Link to="/managers" data-testid="home-pros-cta" className="block">
                    <Button className="rounded-none w-full h-14 px-6 bg-[#E11D2A] hover:bg-[#B0121E] text-white font-bold tracking-wide text-base font-['Manrope']">
                        SEE PRO SERVICES
                        <ArrowUpRight className="w-5 h-5 ml-2" strokeWidth={2.5} />
                    </Button>
                </Link>
                <a href={SITE.phoneHref} data-testid="home-pros-call" className="block">
                    <Button variant="outline" className="rounded-none w-full h-14 px-6 bg-transparent border-2 border-[#F6F5F2] hover:bg-[#F6F5F2] hover:text-[#111111] text-[#F6F5F2] font-bold tracking-wide text-base font-['Manrope']">
                        <Phone className="w-4 h-4 mr-2" strokeWidth={2.5} />
                        CALL {SITE.phoneDisplay}
                    </Button>
                </a>
            </div>
        </div>
    </section>
);

/* ---------- Social / Connect ---------- */
const Social = () => (
    <section id="connect" className="bg-[#F6F5F2] border-b border-[#E2DFD8]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-10 md:py-14">
            <div className="relative max-w-4xl mx-auto border-2 border-[#111111] hover:shadow-block transition-all">
                <img
                    src="https://customer-assets.emergentagent.com/job_service-connect-473/artifacts/0mq7zko6_social.png"
                    alt="Check us out on YouTube and Follow us on Facebook — youtube.com/@5starhelp · facebook.com/5starservicellc"
                    className="block w-full h-auto"
                />
                <a
                    href={SITE.youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open 5 Star Help on YouTube in a new tab"
                    className="absolute inset-y-0 left-0 w-1/2 hover:bg-[#E11D2A]/5 transition-colors"
                    data-testid="social-youtube"
                />
                <a
                    href={SITE.facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open 5 Star Construction Services on Facebook in a new tab"
                    className="absolute inset-y-0 right-0 w-1/2 hover:bg-[#1877F2]/5 transition-colors"
                    data-testid="social-facebook"
                />
            </div>
        </div>
    </section>
);

/* ---------- Page ---------- */
export default function Home() {
    useSeo({
        title: "Medford Handyman & General Contractor · Decks, Fans, Solartubes, Drywall | 5 Star Construction Services LLC",
        description: "Licensed Medford, Oregon handyman & general contractor (CCB #232477). Decks & deck repair, whole house fans, solartube skylights, drywall & paint, manufactured-home setup, and property maintenance across Medford, Ashland, Central Point, Grants Pass, and the Rogue Valley. 24-hour emergency calls — 541-292-9620.",
        path: "/",
        jsonLdKey: "home",
        jsonLd: [
            {
                "@context": "https://schema.org",
                "@type": "WebSite",
                name: "5 Star Construction Services LLC",
                url: "https://5starhelpme.com",
                potentialAction: {
                    "@type": "SearchAction",
                    target: "https://5starhelpme.com/faq?q={search_term_string}",
                    "query-input": "required name=search_term_string",
                },
            },
            breadcrumbSchema([{ name: "Home", path: "/" }]),
        ],
    });
    return (
        <div className="bg-[#F6F5F2] min-h-screen">
            <AnnouncementBar />
            <Header />
            <main>
                <Hero />
                <Services />
                <ForProsCallout />
                <About />
                <Testimonials />
                <Social />
                <Contact />
            </main>
            <Footer />
        </div>
    );
}
